import React, { useRef, useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api, STORE_SLUG, BACK_OPTION_LABELS } from "../lib/api";
import { useCart } from "../context/CartContext";
import { CreditCard, MapPin, Truck, Loader2, Shirt, Minus, Plus, Trash2 } from "lucide-react";

export default function Checkout() {
  const { items, subtotal, clear, updateItem, updateQty, removeItem } = useCart();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [paypal, setPaypal] = useState({ enabled: false, client_id: "" });
  const [method, setMethod] = useState("stripe"); // stripe | paypal
  const [form, setForm] = useState({ customer_name: "", email: "", phone: "" });
  const [ship, setShip] = useState({ address: "", city: "", state: "", zip: "" });
  const [fulfillment, setFulfillment] = useState("pickup");
  const [pickupLocation, setPickupLocation] = useState("Practice Pickup");
  const [pickupTeam, setPickupTeam] = useState("");
  const [loading, setLoading] = useState(false);
  const placedRef = useRef(false);

  useEffect(() => {
    api.get(`/stores/${STORE_SLUG}`).then((r) => setStore(r.data));
    api.get(`/paypal/config`).then((r) => setPaypal(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (items.length === 0 && !placedRef.current) navigate("/shop/shop-all");
  }, [items, navigate]);
  if (items.length === 0) return null;

  const seniorOnly = items.length > 0 && items.every((it) => it.player_id);
  const pickupOptions = seniorOnly ? ["Practice Pickup", "Parent Delivery"] : (store?.pickup_locations || ["Practice Pickup", "Game Pickup"]);  const shipCost = fulfillment === "shipping" ? (store?.shipping?.usps ?? 8) : 0;
  const total = subtotal + shipCost;
  const taxStatePct = store?.tax_state_pct ?? 6;
  const taxSalesPct = store?.tax_sales_pct ?? 9;
  const taxState = subtotal * (taxStatePct / 100);
  const taxSales = subtotal * (taxSalesPct / 100);
  const pretax = subtotal - taxState - taxSales;
  const customItems = items.filter((it) => it.back_option === "name_number");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setS = (k) => (e) => setShip({ ...ship, [k]: e.target.value });

  const validate = () => {
    if (!form.customer_name || !form.email) { toast.error("Enter your name and email"); return false; }
    for (const it of customItems) {
      if (!it.player_name || !it.player_number || !it.team) { toast.error("Add player name, number & team for your custom tee"); return false; }
    }
    if (fulfillment === "pickup" && !pickupLocation) { toast.error("Choose a pickup option"); return false; }
    if (fulfillment === "pickup" && !pickupTeam && !seniorOnly) { toast.error("Select which team this order is for"); return false; }
    if (fulfillment === "shipping" && (!ship.address || !ship.city || !ship.state || !ship.zip)) { toast.error("Enter your full shipping address"); return false; }
    return true;
  };

  const buildPayload = (payMethod) => ({
    store_slug: STORE_SLUG, ...form, payment_method: payMethod,
    fulfillment, pickup_location: fulfillment === "pickup" ? pickupLocation : "",
    pickup_team: fulfillment === "pickup" ? pickupTeam : "",
    shipping_address: fulfillment === "shipping" ? `${ship.address}, ${ship.city}, ${ship.state} ${ship.zip}` : "",
    origin_url: window.location.origin,
    items: items.map(({ _id, ...rest }) => rest),
  });

  const submitStripe = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", buildPayload("stripe"));
      placedRef.current = true;
      if (data.checkout_url) {
        localStorage.setItem("gv_pending_order", data.order_number);
        clear();
        window.location.href = data.checkout_url;
      } else { toast.error("Could not start checkout"); setLoading(false); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-black border border-white/10 focus:border-[var(--gv-blue)] outline-none px-4 py-3 text-sm";
  const optCls = (active) => `w-full flex items-center gap-3 px-4 py-3 border text-left transition-colors ${active ? "border-[var(--gv-blue)] bg-[var(--gv-blue)]/10" : "border-white/10"}`;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="font-display text-5xl mb-1">CHECKOUT</h1>
      <p className="text-xs uppercase tracking-[0.25em] text-[var(--gv-blue-bright)] font-bold mb-8">Gray Collegiate Academy • War Eagles</p>
      <div className="space-y-6">
        {/* Item review */}
        <div className="border border-white/10 bg-[#141518] p-4 space-y-3" data-testid="order-items">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400">Your Items ({items.length})</p>
            <Link to="/shop/shop-all" data-testid="add-more-link" className="text-xs text-[var(--gv-blue-bright)] font-bold">+ Add more</Link>
          </div>
          {items.map((it) => (
            <div key={it._id} className="flex gap-3 items-center" data-testid="checkout-line-item">
              <img src={it.front_image} alt={it.product_name} className="w-14 h-14 object-cover bg-black flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">{it.product_name}</p>
                <p className="text-xs text-gray-400">{it.player_id ? `${it.category_label} • Size ${it.size}` : `${it.garment_color}${it.front_design_label ? ` • ${it.front_design_label}` : ""} • ${BACK_OPTION_LABELS[it.back_option]} • ${it.size}`}</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex items-center border border-white/10">
                    <button onClick={() => updateQty(it._id, it.quantity - 1)} data-testid="ci-minus" className="p-1"><Minus className="w-3 h-3" /></button>
                    <span className="w-7 text-center text-xs font-bold">{it.quantity}</span>
                    <button onClick={() => updateQty(it._id, it.quantity + 1)} data-testid="ci-plus" className="p-1"><Plus className="w-3 h-3" /></button>
                  </div>
                  <button onClick={() => removeItem(it._id)} data-testid="ci-remove" className="text-gray-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="text-sm font-bold text-[var(--gv-blue-bright)]">${(it.unit_price * it.quantity).toFixed(2)}</div>
            </div>
          ))}
        </div>

        <div className="border border-white/10 bg-[#141518] p-6 space-y-3">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400">Contact</p>
          <input placeholder="Full Name" value={form.customer_name} onChange={set("customer_name")} data-testid="checkout-name" className={inputCls} />
          <input placeholder="Email" value={form.email} onChange={set("email")} data-testid="checkout-email" className={inputCls} />
          <input placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} data-testid="checkout-phone" className={inputCls} />
        </div>

        {customItems.length > 0 && (
          <div className="border border-[var(--gv-blue)]/40 bg-[var(--gv-blue)]/5 p-6 space-y-4" data-testid="custom-info-section">
            <div className="flex items-center gap-2">
              <Shirt className="w-5 h-5 text-[var(--gv-blue-bright)]" />
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--gv-blue-bright)]">Custom Player Number Details</p>
            </div>
            <p className="text-xs text-gray-300 -mt-1">Custom name & number tees are made to order — please allow <span className="font-bold text-white">5–7 business days</span> before pickup or shipping. All other shirts are in stock and ready right away.</p>
            {customItems.map((it) => (
              <div key={it._id} className="space-y-2 border-t border-white/10 pt-3 first:border-t-0 first:pt-0" data-testid="custom-item">
                <p className="text-sm font-bold">{it.product_name} <span className="text-gray-400 font-normal">• {it.garment_color} • Size {it.size}</span></p>
                <div className="grid sm:grid-cols-2 gap-2">
                  <input placeholder="Player Name" value={it.player_name || ""} onChange={(e) => updateItem(it._id, { player_name: e.target.value })} data-testid="checkout-player-name" className={inputCls} />
                  <input placeholder="Player Number" value={it.player_number || ""} onChange={(e) => updateItem(it._id, { player_number: e.target.value })} data-testid="checkout-player-number" className={inputCls} />
                </div>
                <select value={it.team || ""} onChange={(e) => updateItem(it._id, { team: e.target.value })} data-testid="checkout-team" className={inputCls}>
                  <option value="">Select Team</option>
                  {(store?.teams || []).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}

        <div className="border border-white/10 bg-[#141518] p-6 space-y-3">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-400">Delivery</p>
          <button onClick={() => setFulfillment("pickup")} data-testid="fulfill-pickup" className={optCls(fulfillment === "pickup")}>
            <MapPin className="w-5 h-5 flex-shrink-0" /><span className="text-sm font-bold">Team Pickup — Free</span>
          </button>
          {fulfillment === "pickup" && (
            <div className="pl-2 grid grid-cols-2 gap-2" data-testid="pickup-options">
              {pickupOptions.map((loc) => (
                <button key={loc} onClick={() => setPickupLocation(loc)} data-testid={`pickup-${loc.replace(/\s/g, "-").toLowerCase()}`}
                  className={`py-3 border text-sm font-bold transition-colors ${pickupLocation === loc ? "border-[var(--gv-blue)] bg-[var(--gv-blue)]/10 text-white" : "border-white/10 text-gray-300"}`}>{loc}</button>
              ))}
            </div>
          )}
          {fulfillment === "pickup" && !seniorOnly && (
            <div className="pl-2" data-testid="pickup-team-block">
              <p className="text-xs uppercase tracking-wider text-gray-400 mb-1.5">Which team is this order for?</p>
              <div className="grid grid-cols-3 gap-2">
                {(store?.teams?.length ? store.teams : ["MS", "JV", "V"]).map((t) => (
                  <button key={t} onClick={() => setPickupTeam(t)} data-testid={`pickup-team-${t}`}
                    className={`py-3 border text-sm font-bold transition-colors ${pickupTeam === t ? "border-[var(--gv-blue)] bg-[var(--gv-blue)]/10 text-white" : "border-white/10 text-gray-300"}`}>{t}</button>
                ))}
              </div>
            </div>
          )}
          <button onClick={() => setFulfillment("shipping")} data-testid="fulfill-shipping" className={optCls(fulfillment === "shipping")}>
            <Truck className="w-5 h-5 flex-shrink-0" /><span className="text-sm font-bold">USPS Shipping — ${(store?.shipping?.usps ?? 8).toFixed(2)}</span>
          </button>
          {fulfillment === "shipping" && (
            <div className="pl-2 space-y-2" data-testid="shipping-fields">
              <input placeholder="Street Address" value={ship.address} onChange={setS("address")} data-testid="ship-address" className={inputCls} />
              <div className="grid grid-cols-3 gap-2">
                <input placeholder="City" value={ship.city} onChange={setS("city")} data-testid="ship-city" className={inputCls} />
                <input placeholder="State" value={ship.state} onChange={setS("state")} data-testid="ship-state" className={inputCls} />
                <input placeholder="ZIP" value={ship.zip} onChange={setS("zip")} data-testid="ship-zip" className={inputCls} />
              </div>
            </div>
          )}
        </div>

        <div className="border border-white/10 bg-[#141518] p-6">
          <div className="flex justify-between text-sm mb-2"><span className="text-gray-400">Subtotal</span><span className="font-bold">${pretax.toFixed(2)}</span></div>
          <div className="flex justify-between text-sm mb-1"><span className="text-gray-400">State Tax</span><span className="font-bold" data-testid="tax-state">${taxState.toFixed(2)}</span></div>
          <div className="flex justify-between text-sm mb-2"><span className="text-gray-400">Sales Tax</span><span className="font-bold" data-testid="tax-sales">${taxSales.toFixed(2)}</span></div>
          {shipCost > 0 && <div className="flex justify-between text-sm mb-4"><span className="text-gray-400">USPS Shipping</span><span className="font-bold" data-testid="ship-cost">${shipCost.toFixed(2)}</span></div>}
          <div className="flex justify-between text-lg font-bold mb-4 border-t border-white/10 pt-3"><span>Total</span><span data-testid="checkout-total">${total.toFixed(2)}</span></div>

          {/* Payment method */}
          {paypal.enabled && (
            <div className="grid grid-cols-2 gap-2 mb-4" data-testid="pay-method">
              <button onClick={() => setMethod("stripe")} data-testid="pay-stripe" className={`py-2 border text-xs font-bold uppercase tracking-widest transition-colors ${method === "stripe" ? "border-[var(--gv-blue)] bg-[var(--gv-blue)]/10 text-white" : "border-white/10 text-gray-400"}`}>Card / Apple Pay</button>
              <button onClick={() => setMethod("paypal")} data-testid="pay-paypal" className={`py-2 border text-xs font-bold uppercase tracking-widest transition-colors ${method === "paypal" ? "border-[var(--gv-blue)] bg-[var(--gv-blue)]/10 text-white" : "border-white/10 text-gray-400"}`}>PayPal</button>
            </div>
          )}

          {method === "paypal" && paypal.enabled ? (
            <div data-testid="paypal-buttons">
              <PayPalScriptProvider options={{ clientId: paypal.client_id, currency: "USD", intent: "capture" }}>
                <PayPalButtons
                  style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                  createOrder={async () => {
                    if (!validate()) throw new Error("validation");
                    const { data } = await api.post("/paypal/create-order", buildPayload("paypal"));
                    return data.paypal_order_id;
                  }}
                  onApprove={async (data) => {
                    try {
                      const { data: cap } = await api.post(`/paypal/capture/${data.orderID}`);
                      if (cap.status === "COMPLETED") {
                        placedRef.current = true;
                        localStorage.setItem("gv_pending_order", cap.order_number || "");
                        clear();
                        navigate(`/payment/success?session_id=${data.orderID}`);
                      } else { toast.error("Payment not completed"); }
                    } catch { toast.error("Could not capture PayPal payment"); }
                  }}
                  onError={() => toast.error("PayPal payment failed")}
                />
              </PayPalScriptProvider>
            </div>
          ) : (
            <button onClick={submitStripe} disabled={loading} data-testid="place-order-btn"
              className="w-full flex items-center justify-center gap-2 bg-[var(--gv-blue)] hover:bg-[var(--gv-blue-bright)] text-black font-bold uppercase tracking-widest text-sm py-4 transition-colors disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              Pay Now — ${total.toFixed(2)}
            </button>
          )}
          <p className="text-center text-xs text-gray-400 mt-3">Secure checkout — Card, Apple Pay & Google Pay via Stripe{paypal.enabled ? " or PayPal" : ""}</p>
          <p className="text-center text-xs text-gray-500 mt-2">Official team store powered by <span className="text-white font-bold">Dad Jerzeys</span></p>
        </div>
      </div>
    </div>
  );
}
